import { API } from "../../config";
import axios from "axios";

export const getTrackingConfig = (brandName) => {
  return fetch(`${API}/v1/tracking_pages/config?brandName=${brandName}`, {
    mode: "cors",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  })
    .then((response) => response.json())
    .catch((error) => {
      console.log(error);
    });
};

/**
 * @typedef {Object} TrackingInfoResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} message - Response message
 * @property {string} object - Type of object returned
 * @property {string} id - Tracking ID
 * @property {string} waybill_id - Waybill ID
 * @property {Object} courier - Courier information
 * @property {string} courier.company - Courier company name
 * @property {string|null} courier.name - Courier name
 * @property {string|null} courier.phone - Courier phone
 * @property {string|null} courier.driver_name - Driver name
 * @property {string|null} courier.driver_phone - Driver phone
 * @property {string|null} courier.driver_plate_number - Driver plate number
 * @property {string|null} courier.driver_photo_url - Driver photo URL
 * @property {Object} origin - Origin information
 * @property {string} origin.contact_name - Origin contact name
 * @property {string} origin.address - Origin address
 * @property {Object} destination - Destination information
 * @property {string} destination.contact_name - Destination contact name
 * @property {string} destination.address - Destination address
 * @property {Array<{note: string, updated_at: string, status: string}>} history - Tracking history
 * @property {string} link - Tracking link
 * @property {string|null} tracking_link_from_courier - Courier tracking link
 * @property {string} order_id - Order ID
 * @property {string} status - Current status
 * @property {string} duration - Delivery duration estimate
 * @property {Object} page_config - Page configuration
 * @property {string} page_config.header_logo - Header logo URL
 * @property {string} page_config.header_background_color - Header background color
 * @property {string} page_config.header_background_text_color - Header text color
 * @property {string} page_config.header_background_btn_text - Header button text
 * @property {boolean} page_config.hide_default_logo - Whether to hide default logo
 * @property {string} page_config.body_background_background_color - Body background color
 * @property {string} page_config.body_background_text_color - Body text color
 * @property {string} page_config.body_background_button_color - Body button color
 * @property {string} page_config.body_background_font_type - Body font type
 * @property {string} page_config.footer_background_color - Footer background color
 * @property {string} page_config.footer_text_color - Footer text color
 * @property {string} page_config.link - Page link
 * @property {boolean} page_config.use_courier_tracking_link - Whether to auto redirect user to courier tracking link/fallback link
 * @property {string|null} page_config.fallback_tracking_link - Fallback tracking link
 * @property {string} organization_name - Organization name
 * @property {Array<{id: string, title: string, body: string, cta: {text: string, url: string}|null, style: {preset: string, custom_hex: string}}>} tracking_banners - Admin-authored notices this merchant has enabled
 */

/**
 * @typedef {Object} TrackingInfo
 * @property {string} id - Tracking ID
 * @property {string} waybillId - Waybill ID
 * @property {string} organizationName - Organization name
 * @property {string} courierCompany - Courier company name
 * @property {string|null} courierName - Courier name
 * @property {string|null} courierPhone - Courier phone number
 * @property {string|null} courierDriverName - Driver name
 * @property {string|null} courierDriverPhone - Driver phone number
 * @property {string|null} courierDriverPlateNumber - Driver's vehicle plate number
 * @property {string|null} courierDriverPhotoUrl - URL to driver's photo
 * @property {string} originContactName - Origin contact name
 * @property {string} originAddress - Origin address
 * @property {string} destinationContactName - Destination contact name
 * @property {string} destinationAddress - Destination address
 * @property {Array<{note: string, updatedAt: string, status: string}>} history - Tracking history
 * @property {string|null} link - Tracking link
 * @property {string|null} trackingLinkFromCourier - Courier's tracking link
 * @property {string} orderId - Order ID
 * @property {string} status - Current tracking status
 * @property {string} referenceId - Reference ID
 * @property {string} duration - Delivery duration estimate
 * @property {string} configHeaderLogo - Header logo URL
 * @property {boolean} configHideDefaultLogo - Whether to hide default logo
 * @property {string} configBodyTextColor - Body text color (defaults to "#6E32BF")
 * @property {boolean} configUseCourierTrackingLink - Whether to use courier tracking link
 * @property {string|null} configFallbackTrackingLink - Fallback tracking link
 * @property {Array<Object>} trackingBanners - Admin-authored notices, in stack order
 */

/**
 * @param {{trackingId: string, environment: string}} params
 * @returns {Promise<TrackingInfo>}
 */
export const getTrackingInfo = async ({ trackingId, environment }) => {
  const query = new URLSearchParams({});
  if (!!environment) query.set("environment", environment);

  /** @type {{data: TrackingInfoResponse}} */
  const response = await axios.get(
    `${API}/v1/trackings/tracking_pages/${trackingId}?${query.toString()}`,
  );

  return {
    id: response.data["id"],
    waybillId: response.data["waybill_id"],
    organizationName: response.data["organization_name"],
    courierCompany: response.data.courier["company"],
    courierName: response.data.courier["name"],
    courierPhone: response.data.courier["phone"],
    courierDriverName: response.data.courier["driver_name"],
    courierDriverPhone: response.data.courier["driver_phone"],
    courierDriverPlateNumber: response.data.courier["driver_plate_number"],
    courierDriverPhotoUrl: response.data.courier["driver_photo_url"],
    originContactName: response.data.origin["contact_name"],
    originAddress: response.data.origin["address"],
    destinationContactName: response.data.destination["contact_name"],
    destinationAddress: response.data.destination["address"],
    history: response.data["history"].map((i) => ({
      note: i.note,
      updatedAt: i.updated_at,
      status: i.status,
    })),
    link: response.data["link"],
    trackingLinkFromCourier: response.data["tracking_link_from_courier"],
    orderId: response.data["order_id"],
    status: response.data["status"],
    referenceId: response.data["reference_id"],
    duration: response.data["duration"],
    configHeaderLogo: response.data.page_config["header_logo"],
    configHideDefaultLogo: response.data.page_config["hide_default_logo"],
    configBodyTextColor:
      response.data.page_config["body_background_text_color"] || "#6E32BF",
    configUseCourierTrackingLink:
      response.data.page_config["use_courier_tracking_link"],
    configFallbackTrackingLink:
      response.data.page_config["fallback_tracking_link"],
    // Absent for any merchant who has enabled none, which is the default.
    trackingBanners: response.data["tracking_banners"] || [],
  };
};
